import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GiKnifeFork, GiCupcake, GiPartyPopper, GiHotMeal, GiCakeSlice, GiDiamondRing, GiSpotedFlower, GiPartyFlags, GiBalloons } from 'react-icons/gi';
import { FiShoppingBag, FiMapPin, FiCalendar, FiArrowRight, FiGift, FiHeart, FiChevronLeft, FiChevronRight, FiSearch } from 'react-icons/fi';
import { FaBaby, FaChild, FaBirthdayCake, FaGlassCheers, FaRing, FaRegHeart, FaStar, FaCheck, FaTimes } from 'react-icons/fa';
import { collection, getDocs, query, where, limit, orderBy } from 'firebase/firestore';
import { db } from '../firebase/client';
import SEO from '../components/SEO';
import HeroSlider from '../components/HeroSlider';
import ServicesSlider from '../components/ServicesSlider';
import AboutSection from '../components/AboutSection';
import FloatingCoupon from '../components/FloatingCoupon';
import { getAllServices, getServicesByCategory } from '../services/serviceService';
import { getAllCategories } from '../services/categoryService';

// Circular Loading Component
const CircularLoading = () => (
  <div className="flex justify-center items-center min-h-[200px]">
    <div className="relative w-16 h-16">
      {/* Outer circle */}
      <div className="absolute top-0 left-0 w-full h-full border-4 border-pink-200 rounded-full"></div>
      {/* Spinning circle */}
      <div className="absolute top-0 left-0 w-full h-full border-4 border-pink-500 rounded-full animate-spin border-t-transparent"></div>
      {/* Inner dot */}
      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
        <div className="w-2 h-2 bg-pink-500 rounded-full animate-pulse"></div>
      </div>
    </div>
  </div>
);

// Loading Skeleton Component
const LoadingSkeleton = () => (
  <div className="animate-pulse">
    <div className="rounded-xl overflow-hidden aspect-square bg-gray-200 mb-4"></div>
    <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
    <div className="h-4 bg-gray-200 rounded w-1/4"></div>
  </div>
);

// Service Category Section Component
const ServiceCategorySection = ({ category, services, loading }) => {
  const [displayedServices, setDisplayedServices] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  
  // Determine how many items to display based on screen size
  const itemsPerPage = 4;
  
  useEffect(() => {
    if (services.length > 0) {
      const endIndex = Math.min(startIndex + itemsPerPage, services.length);
      setDisplayedServices(services.slice(startIndex, endIndex));
    }
  }, [services, startIndex]);
  
  const nextSlide = () => {
    const newIndex = startIndex + itemsPerPage;
    if (newIndex < services.length) {
      setStartIndex(newIndex);
    } else {
      setStartIndex(0); // Loop back to start
    }
  };
  
  const prevSlide = () => {
    const newIndex = startIndex - itemsPerPage;
    if (newIndex >= 0) {
      setStartIndex(newIndex);
    } else {
      setStartIndex(Math.max(0, services.length - itemsPerPage)); // Go to last page
    }
  };
  
  if (loading) {
    return <CircularLoading />;
  }

  if (services.length === 0) return null;
  
  // Helper function to get the image URL
  const getServiceImage = (service) => {
    if (service.images && Array.isArray(service.images) && service.images.length > 0) {
      return service.images[0];
    }
    if (service.imageURL) return service.imageURL;
    if (service.image) return service.image;
    return "/images/placeholder-service.jpg";
  };
  
  return (
    <div className="py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold tracking-tight font-heading text-center" style={{ color: 'var(--text-primary)' }}>
            {category.name}
          </h2>
          <p className="mt-4 text-lg text-center" style={{ color: 'var(--text-secondary)' }}>
            {category.description || `Beautiful decoration services for ${category.name.toLowerCase()}`}
          </p>
        </div>
        
        <div className="relative">
          {/* Navigation buttons */}
          {services.length > itemsPerPage && (
            <>
              <button 
                onClick={prevSlide}
                className="absolute left-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-pink-50 transition-colors -ml-4"
                aria-label="Previous"
              >
                <FiChevronLeft size={24} className="text-gray-500" />
              </button>
              <button 
                onClick={nextSlide}
                className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white rounded-full p-2 shadow-md hover:bg-pink-50 transition-colors -mr-4"
                aria-label="Next"
              >
                <FiChevronRight size={24} className="text-gray-500" />
              </button>
            </>
          )}
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {loading ? (
              // Loading skeleton placeholders
              Array(4).fill().map((_, index) => (
                <LoadingSkeleton key={index} />
              ))
            ) : (
              displayedServices.map((service) => (
                <Link key={service.id} to={`/booking/${service.id}`} className="block group relative cursor-pointer">
                  <div className="relative overflow-hidden shadow-md">
                    <img 
                      src={getServiceImage(service)}
                      alt={service.name}
                      className="w-full aspect-square object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/images/placeholder-service.jpg';
                      }}
                    />
                    <div className="absolute bottom-3 right-3">
                      <button className="bg-white p-2 shadow-md hover:bg-pink-50 transition-colors">
                        <FaRegHeart className="text-gray-500 hover:text-pink-500" />
                      </button>
                    </div>
                  </div>
                  <div className="mt-4">
                    <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                      {service.name}
                    </h3>
                    <div className="flex items-center mt-1">
                      <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        {service.location || "At your location"}
                      </p>
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-lg font-semibold" style={{ color: 'var(--primary)' }}>
                        {service.price ? `₹${service.price}` : (service.basePrice ? `₹${service.basePrice}` : 'Price on request')}
                      </p>
                      {(service.rating || service.reviews) && (
                        <div className="flex items-center">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <FaStar 
                              key={i} 
                              className={i < (parseInt(service.rating) || 5) ? "text-yellow-400" : "text-gray-300"} 
                              size={14} 
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Service Features */}
                    {service.features && service.features.length > 0 && (
                      <div className="mt-2">
                        <ul className="space-y-1">
                          {service.features.slice(0, 2).map((feature, index) => {
                            // Handle both old string format and new object format
                            const featureText = typeof feature === 'string' ? feature : feature.text;
                            const featureActive = typeof feature === 'string' ? true : feature.active;
                            
                            return (
                              <li key={index} className="flex items-start">
                                {featureActive ? (
                                  <FaCheck className="text-green-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                ) : (
                                  <FaTimes className="text-red-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                )}
                                <span className={`text-xs line-clamp-1 ${featureActive ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                                  {featureText}
                                </span>
                              </li>
                            );
                          })}
                        </ul>
                        {service.features.length > 2 && (
                          <p className="text-xs text-gray-500 mt-1">+{service.features.length - 2} more features</p>
                        )}
                      </div>
                    )}
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
        
        <div className="text-center mt-10">
          <Link 
            to={`/category/${category.slug || category.name.toLowerCase().replace(/\s+/g, '-')}`}
            className="inline-flex items-center text-lg font-medium"
            style={{ color: 'var(--primary)' }}
          >
            View All {category.name} <FiArrowRight className="ml-2" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default function Home() {
  const [featuredBranches, setFeaturedBranches] = useState([]);
  const [popularAddOns, setPopularAddOns] = useState([]);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [categoryServices, setCategoryServices] = useState({});
  const [mixedServices, setMixedServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const categorySliderRef = useRef(null);
  const serviceSliderRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const searchRef = useRef(null);
  
  // Helper function to get appropriate icon for each category - moved inside the component
  const getCategoryIcon = (categoryName) => {
    const name = categoryName.toLowerCase();
    
    if (name.includes('birthday')) return <FaBirthdayCake />;
    if (name.includes('anniversary')) return <FaGlassCheers />;
    if (name.includes('baby shower')) return <FaBaby />;
    if (name.includes('welcome baby')) return <FaBaby />;
    if (name.includes('kids')) return <FaChild />;
    if (name.includes('premium')) return <FiGift />;
    if (name.includes('bachelor') || name.includes('bachelorette')) return <FaGlassCheers />;
    if (name.includes('wedding')) return <FaRing />;
    if (name.includes('proposal') || name.includes('romantic')) return <FiHeart />;
    if (name.includes('balloon')) return <GiBalloons />;
    if (name.includes('party')) return <GiPartyPopper />;
    if (name.includes('flower')) return <GiSpotedFlower />;
    
    // Default icon
    return <GiPartyFlags />;
  };
  
  // Helper function to get the service image URL
  const getServiceImage = (service) => {
    if (service.images && Array.isArray(service.images) && service.images.length > 0) {
      return service.images[0];
    }
    if (service.imageURL) return service.imageURL;
    if (service.image) return service.image;
    return "/images/placeholder-service.jpg";
  };
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const fetchHomeData = async () => {
      try {
        setLoading(true);

        // Fetch all data in parallel
        const [allServices, allCategories, branchesSnapshot, addOnsSnapshot] = await Promise.all([
          getAllServices(true),
          getAllCategories(true),
          getDocs(query(collection(db, 'branches'), where('isActive', '==', true), limit(3))),
          getDocs(query(collection(db, 'addOns'), where('isAvailable', '==', true), limit(4)))
        ]);

        // Process branches and addons data
        const branchesList = branchesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        const addOnsList = addOnsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));

        // Process services data efficiently
        const servicesData = {};
        const combinedServices = [...allServices];
        
        // Use a single pass through services to organize them
        const categoryMap = new Map();
        allServices.forEach(service => {
          if (service.categoryId) {
            if (!categoryMap.has(service.categoryId)) {
              categoryMap.set(service.categoryId, []);
            }
            categoryMap.get(service.categoryId).push(service);
          }
        });

        // Process category services in a single pass
        allCategories.forEach(category => {
          const categoryServices = categoryMap.get(category.id) || [];
          if (categoryServices.length > 0) {
            servicesData[category.id] = categoryServices.slice(0, 8);
          }
        });

        // Shuffle services efficiently using Fisher-Yates algorithm
        const shuffleArray = (array) => {
          for (let i = array.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [array[i], array[j]] = [array[j], array[i]];
          }
          return array;
        };

        const shuffledServices = shuffleArray([...combinedServices]);
        const randomServices = shuffledServices.slice(0, 20);
        const sliderServices = shuffledServices.slice(20, 26); // Take next 6 for slider

        // Update all states at once
        setFeaturedBranches(branchesList);
        setPopularAddOns(addOnsList);
        setServices(sliderServices);
        setCategories(allCategories || []);
        setCategoryServices(servicesData);
        setMixedServices(randomServices);
        
        // Small delay to ensure smooth transition
        setTimeout(() => {
          setLoading(false);
        }, 500);

      } catch (error) {
        console.error('Error fetching home data:', error);
        setLoading(false);
      }
    };

    fetchHomeData();
  }, []);

  useEffect(() => {
    if (!loading && categories.length > 0 && !selectedCategory) {
      setSelectedCategory(categories[0]);
    }
  }, [loading, categories, selectedCategory]);

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
    setTimeout(() => {
      serviceSliderRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Generate search suggestions based on categories and services
  const updateSearchSuggestions = (query) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }

    const lowercaseQuery = query.toLowerCase();
    
    // Get suggestions from categories
    const categorySuggestions = categories
      .filter(cat => cat.name.toLowerCase().includes(lowercaseQuery))
      .map(cat => ({
        id: `cat-${cat.id}`,
        text: cat.name,
        type: 'category',
        data: cat
      }));
    
    // Get suggestions from services
    const combinedServices = [];
    
    // Get services from all categories
    Object.values(categoryServices).forEach(services => {
      if (Array.isArray(services)) {
        combinedServices.push(...services);
      }
    });
    
    // Also add mixed services
    if (mixedServices && mixedServices.length) {
      combinedServices.push(...mixedServices);
    }
    
    // Filter services by name
    const serviceSuggestions = combinedServices
      .filter((service, index, self) => 
        // Ensure unique services
        index === self.findIndex(s => s.id === service.id) &&
        service.name.toLowerCase().includes(lowercaseQuery)
      )
      .slice(0, 5) // Limit to 5 services
      .map(service => ({
        id: `service-${service.id}`,
        text: service.name,
        type: 'service',
        data: service
      }));
    
    // Combine and limit suggestions
    const allSuggestions = [...categorySuggestions, ...serviceSuggestions].slice(0, 8);
    
    // Add a "View all results" option if we have many matches
    if (categorySuggestions.length + serviceSuggestions.length > 8) {
      allSuggestions.push({
        id: 'view-all',
        text: `View all results for "${query}"`,
        type: 'viewAll',
        data: { query }
      });
    }
    
    setSearchSuggestions(allSuggestions);
  };

  // Handle search term changes
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    updateSearchSuggestions(value);
    setShowSuggestions(true);
  };

  // Handle search submission
  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality here or navigate to search results page
    console.log('Searching for:', searchTerm);
    setShowSuggestions(false);
    // Example: navigate to search page
    // history.push(`/search?query=${encodeURIComponent(searchTerm)}`);
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    if (suggestion.type === 'category') {
      // Select the category
      handleCategoryClick(suggestion.data);
      setShowSuggestions(false);
      setSearchTerm('');
    } else if (suggestion.type === 'service') {
      // Navigate to service page
      console.log('Navigate to service:', suggestion.data.id);
      // Example: navigate to service page
      // history.push(`/booking/${suggestion.data.id}`);
      window.location.href = `/booking/${suggestion.data.id}`;
    } else if (suggestion.type === 'viewAll') {
      // Navigate to search results
      console.log('View all results for:', suggestion.data.query);
      // Example: navigate to search results page
      // history.push(`/search?query=${encodeURIComponent(suggestion.data.query)}`);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.5,
      }
    },
    hover: {
      y: -10,
      transition: {
        duration: 0.2,
        ease: 'easeInOut'
      }
    }
  };

  return (
    <>
     
      <SEO 
        title="Home"
        description="Welcome to Habba Events - Your premier destination for decoration services and event styling. Transform any celebration into an unforgettable experience."
        keywords={[
          'balloon decorations bangalore',
          'birthday decorations',
          'anniversary decorations',
          'baby shower decorations',
          'event decorations bangalore',
          'Habba Events',
          'party decorations',
          'event styling'
        ]}
        url="/"
      />
      <div className="flex flex-col min-h-screen bg-gradient-to-r from-pink-50 to-purple-50">
        <div className="flex-grow">
         
          {/* Search Bar */}
          <div className="bg-white shadow-sm py-2 md:py-3">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div ref={searchRef} className="relative">
                <form onSubmit={handleSearch} className="flex items-center">
                  <div className="relative flex-grow">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiSearch className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={() => searchTerm.length >= 2 && setShowSuggestions(true)}
                      className="block w-full pl-10 pr-12 py-2 md:py-3 border border-gray-300 rounded-full bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm md:text-base"
                      placeholder="Search for decorations, cakes.."
                      autoComplete="off"
                    />
                    <div className="absolute inset-y-0 right-0 flex items-center">
                      <button
                        type="submit"
                        className="h-full px-4 rounded-r-full bg-pink-500 text-white hover:bg-pink-600 font-medium text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                </form>

                {/* Search Suggestions Dropdown */}
                {showSuggestions && searchSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg z-50 border border-gray-200 overflow-hidden">
                    <ul>
                      {searchSuggestions.map((suggestion) => (
                        <li 
                          key={suggestion.id}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="px-4 py-2 hover:bg-gray-50 cursor-pointer flex items-center"
                        >
                          {suggestion.type === 'category' && (
                            <span className="mr-2 text-pink-500">
                              {getCategoryIcon(suggestion.data.name)}
                            </span>
                          )}
                          {suggestion.type === 'service' && (
                            <div className="mr-3 h-8 w-8 flex-shrink-0 rounded overflow-hidden">
                              <img 
                                src={getServiceImage(suggestion.data)} 
                                alt="" 
                                className="h-full w-full object-cover"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = '/images/placeholder-service.jpg';
                                }}
                              />
                            </div>
                          )}
                          {suggestion.type === 'viewAll' && (
                            <span className="mr-2 text-blue-500">
                              <FiArrowRight />
                            </span>
                          )}
                          <div className="flex-1">
                            <div className="text-sm font-medium">
                              {suggestion.text}
                            </div>
                            {suggestion.type === 'service' && suggestion.data.price && (
                              <div className="text-xs text-gray-500">
                                ₹{suggestion.data.price}
                              </div>
                            )}
                            {suggestion.type === 'category' && (
                              <div className="text-xs text-gray-500">
                                Category
                              </div>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Add the floating coupon component */}
          <FloatingCoupon />

          {/* Floating icons animation */}
          

          

          {/* Horizontal Category Slider */}
          <div className="py-2 md:py-4 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div
                ref={categorySliderRef}
                className="flex overflow-x-auto gap-3 md:gap-6 py-2 md:py-4 no-scrollbar"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
              >
                {loading ? (
                  <CircularLoading />
                ) : (
                  categories.map((category, idx) => (
                    <button
                      key={category.id}
                      onClick={() => handleCategoryClick(category)}
                      className={`flex flex-col items-center min-w-[70px] sm:min-w-[90px] md:min-w-[120px] lg:min-w-[140px] px-2 rounded-lg 
                        ${selectedCategory?.id === category.id ? 'text-green-600' : 'text-gray-700'}`}
                      style={{ flex: '0 0 auto' }}
                    >
                      <div className="flex items-center justify-center text-base sm:text-lg md:text-xl lg:text-2xl mb-1 md:mb-2">
                        {getCategoryIcon(category.name)}
                      </div>
                      <span className="text-[10px] sm:text-xs md:text-sm lg:text-base font-medium text-center mt-1">{category.name}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
          
          {/* Horizontal Services Slider for Selected Category */}
          {selectedCategory && (
            <div ref={serviceSliderRef} className="pb-4 md:pb-8 bg-white">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex overflow-x-auto gap-3 md:gap-6 py-2 md:py-4 no-scrollbar">
                  {(categoryServices[selectedCategory.id] || []).map((service) => (
                    <Link
                      key={service.id}
                      to={`/booking/${service.id}`}
                      className="flex-shrink-0 w-36 sm:w-52 md:w-64 lg:w-72 bg-white rounded-xl shadow-sm hover:shadow transition group"
                      style={{ flex: '0 0 auto' }}
                    >
                      <div className="relative w-full h-24 sm:h-36 md:h-48 lg:h-56 rounded-t-xl overflow-hidden">
                        <img
                          src={getServiceImage(service)}
                          alt={service.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/placeholder-service.jpg';
                          }}
                        />
                        <div className="absolute top-2 right-2">
                          <div className="flex items-center bg-white px-1.5 py-0.5 md:px-2 md:py-1 rounded text-[10px] md:text-xs shadow-sm">
                            <FaStar className="text-yellow-400 mr-0.5 md:mr-1" size={10} />
                            <span>{service.rating || "4.5"}</span>
                          </div>
                        </div>
                      </div>
                      <div className="p-2 md:p-3 lg:p-4">
                        <div className="font-medium text-xs sm:text-sm md:text-base lg:text-lg truncate">{service.name}</div>
                        <div className="flex items-center justify-between mt-1 md:mt-2">
                          <span className="text-pink-600 font-bold text-sm sm:text-base md:text-lg lg:text-xl">
                            ₹ {service.price || service.basePrice || '999'}
                          </span>
                          <span className="text-[10px] md:text-xs text-gray-400 line-through">
                            {service.mrp ? `₹ ${service.mrp}` : (service.originalPrice ? `₹ ${service.originalPrice}` : '')}
                          </span>
                        </div>
                        {/* Service Features */}
                        {service.features && service.features.length > 0 && (
                          <div className="mt-2">
                            <ul className="space-y-1">
                              {service.features.slice(0, 2).map((feature, index) => {
                                // Handle both old string format and new object format
                                const featureText = typeof feature === 'string' ? feature : feature.text;
                                const featureActive = typeof feature === 'string' ? true : feature.active;
                                
                                return (
                                  <li key={index} className="flex items-start">
                                    {featureActive ? (
                                      <FaCheck className="text-green-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                    ) : (
                                      <FaTimes className="text-red-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                    )}
                                    <span className={`text-xs line-clamp-1 ${featureActive ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                                      {featureText}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                            {service.features.length > 2 && (
                              <p className="text-xs text-gray-500 mt-1">+{service.features.length - 2} more features</p>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="text-center mt-2 md:mt-4">
                  <Link
                    to={`/category/${selectedCategory.slug || selectedCategory.name.toLowerCase().replace(/\s+/g, '-')}`}
                    className="inline-flex items-center text-xs sm:text-sm md:text-base font-medium"
                    style={{ color: 'var(--primary)' }}
                  >
                    View All {selectedCategory.name} <FiArrowRight className="ml-1 md:ml-2" />
                  </Link>
                </div>
              </div>
            </div>
          )}
          
          {/* Mixed Services Display */}
          <div className="py-16">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-12">
                <h2 className="text-3xl font-bold text-pink-600">
                  Explore Our Decoration Services
                </h2>
                <p className="mt-4 text-lg text-gray-600">
                  Beautiful decorations for all your special occasions
                </p>
              </div>
              
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
                {loading ? (
                  <div className="col-span-full">
                    <CircularLoading />
                  </div>
                ) : (
                  mixedServices.map((service) => (
                    <Link key={service.id} to={`/booking/${service.id}`} className="block group relative cursor-pointer">
                      <div className="relative overflow-hidden shadow-md">
                        <img 
                          src={getServiceImage(service)}
                          alt={service.name}
                          className="w-full aspect-square object-cover object-center group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/placeholder-service.jpg';
                          }}
                        />
                        <div className="absolute bottom-3 right-3">
                          <button className="bg-white p-2 shadow-md hover:bg-pink-50 transition-colors">
                            <FaRegHeart className="text-gray-500 hover:text-pink-500" />
                          </button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <h3 className="text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                          {service.name}
                        </h3>
                        <div className="flex items-center mt-1">
                          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            {service.location || "At your location"}
                          </p>
                        </div>
                        <div className="flex items-center justify-between mt-2">
                          <p className="text-lg font-semibold" style={{ color: 'var(--primary)' }}>
                            {service.price ? `₹${service.price}` : (service.basePrice ? `₹${service.basePrice}` : 'Price on request')}
                          </p>
                          {(service.rating || service.reviews) && (
                            <div className="flex items-center">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <FaStar 
                                  key={i} 
                                  className={i < (parseInt(service.rating) || 5) ? "text-yellow-400" : "text-gray-300"} 
                                  size={14} 
                                />
                              ))}
                            </div>
                          )}
                        </div>
                        {/* Service Features */}
                        {service.features && service.features.length > 0 && (
                          <div className="mt-2">
                            <ul className="space-y-1">
                              {service.features.slice(0, 2).map((feature, index) => {
                                // Handle both old string format and new object format
                                const featureText = typeof feature === 'string' ? feature : feature.text;
                                const featureActive = typeof feature === 'string' ? true : feature.active;
                                
                                return (
                                  <li key={index} className="flex items-start">
                                    {featureActive ? (
                                      <FaCheck className="text-green-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                    ) : (
                                      <FaTimes className="text-red-500 mt-0.5 mr-1 flex-shrink-0" size={12} />
                                    )}
                                    <span className={`text-xs line-clamp-1 ${featureActive ? 'text-gray-600' : 'text-gray-400 line-through'}`}>
                                      {featureText}
                                    </span>
                                  </li>
                                );
                              })}
                            </ul>
                            {service.features.length > 2 && (
                              <p className="text-xs text-gray-500 mt-1">+{service.features.length - 2} more features</p>
                            )}
                          </div>
                        )}
                      </div>
                    </Link>
                  ))
                )}
              </div>
              
              <div className="text-center mt-10">
                <Link 
                  to="/categories"
                  className="inline-flex items-center text-lg font-medium"
                  style={{ color: 'var(--primary)' }}
                >
                  View All Services <FiArrowRight className="ml-2" />
                </Link>
              </div>
            </div>
          </div>
          
          {/* Original Category-wise Services grid */}
          <div className="py-16 food-pattern-bg">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="lg:text-center">
                <h2 className="text-base font-semibold tracking-wide uppercase" style={{ color: 'var(--primary)' }}>Our Services</h2>
                <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight sm:text-4xl font-heading" style={{ color: 'var(--text-primary)' }}>
                  Category-wise Event Services
                </p>
                <p className="mt-4 max-w-2xl text-xl lg:mx-auto" style={{ color: 'var(--text-secondary)' }}>
                  Explore our range of decoration services tailored for your special occasions
                </p>
              </div>

              <div className="mt-16">
                <div className="grid grid-cols-1 gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-3">
                  {loading ? (
                    // Loading skeleton placeholders
                    Array(6).fill().map((_, index) => (
                      <div key={index} className="pt-6 animate-pulse">
                        <div className="card h-full">
                          <div>
                            <div className="-mt-6 mb-4">
                              <div className="inline-flex items-center justify-center p-3 h-12 w-12 rounded-full bg-gray-200"></div>
                            </div>
                            <div className="h-5 bg-gray-200 rounded w-3/4 mb-3"></div>
                            <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
                            <div className="h-4 bg-gray-200 rounded w-5/6"></div>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    categories.map((category, index) => (
                    <motion.div 
                      key={index}
                      className="pt-6"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, amount: 0.3 }}
                      transition={{ delay: index * 0.1 }}
                      whileHover={{ y: -5 }}
                    >
                      <div className="card h-full">
                        <div>
                          <div className="-mt-6 mb-4">
                            <span 
                              className="inline-flex items-center justify-center p-3 rounded-full shadow-lg"
                              style={{ 
                                background: `linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)`,
                                color: 'white',
                                fontSize: '1.5rem'
                              }}
                            >
                                {getCategoryIcon(category.name)}
                            </span>
                          </div>
                          <h3 className="text-lg font-medium tracking-tight font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                              {category.name}
                          </h3>
                            <p className="mb-3" style={{ color: 'var(--text-secondary)' }}>
                              {category.description || `Beautiful decoration services for ${category.name.toLowerCase()}`}
                            </p>
                            
                            {/* Display subcategories if available */}
                            {category.subcategories && category.subcategories.length > 0 ? (
                              <div className="mt-3">
                                <ul className="space-y-1">
                                  {category.subcategories.slice(0, 3).map((subcategory, idx) => (
                                    <li key={idx} className="flex items-center">
                                      <div className="w-1.5 h-1.5 rounded-full mr-2" style={{ backgroundColor: 'var(--primary)' }}></div>
                                      <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                                        {subcategory.name}
                                      </span>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ) : null}
                            
                            <div className="mt-4">
                              <Link 
                                to={`/category/${category.slug || category.name.toLowerCase().replace(/\s+/g, '-')}`}
                                className="text-sm font-medium inline-flex items-center"
                                style={{ color: 'var(--primary)' }}
                              >
                                View Services <FiArrowRight className="ml-1" />
                              </Link>
                            </div>
                          </div>
                      </div>
                    </motion.div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="divider-dots"></div>

          {/* Testimonials Section */}
          <div className="py-16 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <h2 className="text-center text-3xl font-extrabold font-heading sm:text-4xl" style={{ color: 'var(--text-primary)' }}>
                What Our Customers Say
              </h2>
              <div className="mt-16 grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                {[
                  {
                    initial: "R",
                    name: "Rahul Sharma",
                    location: "Bangalore",
                    comment: "Celebrated my daughter's birthday with Habba Events decorations. The balloon theme was magical, and the setup was flawless. The team went above and beyond!"
                  },
                  {
                    initial: "P",
                    name: "Priya Desai",
                    location: "Mysore",
                    comment: "Our baby shower was a dream come true. The decorations were exactly as I had imagined, and the attention to detail was remarkable. Highly recommend!"
                  },
                  {
                    initial: "A",
                    name: "Arjun Reddy",
                    location: "Bangalore",
                    comment: "Had my anniversary decoration done by Habba Events. The romantic canopy setup was perfect, and every element was Instagram-worthy. An unforgettable evening!"
                  }
                ].map((testimonial, index) => (
                  <motion.div 
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.3 }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ y: -5 }}
                    className="card-funky overflow-hidden h-full"
                  >
                    <div className="px-6 py-8">
                      <div className="flex items-center">
                        <div 
                          className="flex-shrink-0 h-12 w-12 rounded-full flex items-center justify-center"
                          style={{ 
                            background: `linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)`,
                            color: 'white',
                            fontWeight: 'bold',
                            fontSize: '1.25rem'
                          }}
                        >
                          <span>{testimonial.initial}</span>
                        </div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium font-heading" style={{ color: 'var(--text-primary)' }}>{testimonial.name}</h3>
                          <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>{testimonial.location}</p>
                        </div>
                      </div>
                      <div className="mt-4">
                        <div className="flex mb-2">
                          {[...Array(5)].map((_, i) => (
                            <span key={i} className="text-lg" style={{ color: 'var(--secondary)' }}>★</span>
                          ))}
                        </div>
                        <p className="italic" style={{ color: 'var(--text-secondary)' }}>"{testimonial.comment}"</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Newsletter Section */}
          <section className="py-16 relative overflow-hidden">
            <div className="absolute top-0 left-0 right-0 h-2" style={{ background: `linear-gradient(90deg, var(--primary), var(--secondary), var(--accent))` }}></div>
            <div 
              className="absolute inset-0 z-0"
              style={{ backgroundColor: 'var(--primary)', opacity: 0.05 }}
            ></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-extrabold font-heading mb-2" style={{ color: 'var(--text-primary)' }}>
                  Stay Updated
                </h2>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Subscribe to our newsletter for special offers and new decoration themes
                </p>
              </div>
              
              <div className="max-w-md mx-auto">
                <form className="flex">
                  <input 
                    type="email" 
                    placeholder="Enter your email" 
                    className="flex-grow px-4 py-3 rounded-l-full"
                    style={{ 
                      borderColor: 'var(--primary)',
                      borderWidth: '2px',
                      borderRightWidth: '0',
                      backgroundColor: 'white'
                    }}
                  />
                  <motion.button 
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="px-5 py-3 rounded-r-full"
                    style={{ 
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      boxShadow: 'var(--btn-shadow)'
                    }}
                  >
                    Subscribe
                  </motion.button>
                </form>
              </div>

              <div className="flex justify-center mt-8">
                <div className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                  We respect your privacy and will never share your information
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-12 relative confetti overflow-hidden">
            <div 
              className="absolute inset-0 z-0"
              style={{ 
                background: `linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%)`,
                opacity: 0.9
              }}
            ></div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="text-center py-12">
                <motion.h2 
                  className="text-4xl font-bold font-display mb-4 text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                >
                  Ready to Celebrate?
                </motion.h2>
                <motion.p 
                  className="text-xl mb-8 text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                >
                  Book your decoration package today and create memories that last a lifetime!
                </motion.p>
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <Link
                    to="/categories"
                    className="inline-flex items-center px-8 py-3 rounded-full font-medium"
                    style={{ 
                      backgroundColor: 'white',
                      color: 'var(--primary)',
                      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
                    }}
                  >
                    Book Now
                  </Link>
                </motion.div>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
} 